import { apiClient } from './client';
import { PaginatedResponse } from './payments';

export type CashflowProvider = 'click' | 'payme' | 'paylov';

export type CashflowPeriod = 'all' | 'day' | 'week' | 'month' | 'range';

export interface ProviderMetrics {
  provider: CashflowProvider;
  commission: number; // 0.02 = 2%
  gross: number; // Собрано
  net: number; // Чистое = gross × (1 − commission)
  received: number; // Поступило
  remaining: number; // Осталось = net − received
}

export interface CashflowTotals {
  gross: number;
  net: number;
  received: number;
  remaining: number;
}

export interface CashflowTimeseriesPoint {
  date: string; // YYYY-MM-DD
  click: number;
  payme: number;
  paylov: number;
}

export interface CashflowSummary {
  period: { preset: CashflowPeriod; from: string | null; to: string | null };
  providers: ProviderMetrics[];
  total: CashflowTotals;
  timeseries: CashflowTimeseriesPoint[];
}

export interface CashReceipt {
  id: string;
  date: string; // YYYY-MM-DD
  provider: CashflowProvider;
  amount: number;
  comment: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateCashReceiptDto {
  date: string;
  provider: CashflowProvider;
  amount: number;
  comment?: string;
}

export type UpdateCashReceiptDto = Partial<CreateCashReceiptDto>;

export interface CashflowSummaryParams {
  period?: CashflowPeriod;
  from?: string;
  to?: string;
}

export interface GetReceiptsParams extends CashflowSummaryParams {
  page?: number;
  limit?: number;
  provider?: CashflowProvider;
}

export const cashflowApi = {
  async getSummary(params: CashflowSummaryParams): Promise<CashflowSummary> {
    const response = await apiClient.get<CashflowSummary>('/cashflow/summary', { params });
    return response.data;
  },
  async getReceipts(params: GetReceiptsParams): Promise<PaginatedResponse<CashReceipt>> {
    const response = await apiClient.get<PaginatedResponse<CashReceipt>>('/cashflow/receipts', { params });
    return response.data;
  },
  async createReceipt(dto: CreateCashReceiptDto): Promise<CashReceipt> {
    const response = await apiClient.post<CashReceipt>('/cashflow/receipts', dto);
    return response.data;
  },
  async updateReceipt(id: string, dto: UpdateCashReceiptDto): Promise<CashReceipt> {
    const response = await apiClient.patch<CashReceipt>(`/cashflow/receipts/${id}`, dto);
    return response.data;
  },
  async deleteReceipt(id: string): Promise<void> {
    await apiClient.delete(`/cashflow/receipts/${id}`);
  },
};
